import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

export default function ChooseUsername() {
  const { user, refreshUser, needsUsername } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername]   = useState('');
  const [status, setStatus]       = useState(null); // null | 'checking' | 'available' | { error }
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const debounceRef  = useRef(null);
  const abortRef     = useRef(null);

  // Redirect away if they don't actually need a username
  useEffect(() => {
    if (user && !needsUsername) {
      navigate('/', { replace: true });
    }
  }, [user, needsUsername, navigate]);

  // Username availability check (debounced)
  const checkUsername = useCallback((val) => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    const u = val.trim();
    if (!u) { setStatus(null); return; }
    if (u.length < 2) { setStatus({ error: 'At least 2 characters.' }); return; }
    if (u.length > 16) { setStatus({ error: 'Max 16 characters.' }); return; }
    if (!/^[a-zA-Z0-9_.\-]+$/.test(u)) { setStatus({ error: 'Letters, numbers, _ . - only.' }); return; }
    setStatus('checking');
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await axios.get(`${API}/auth/check-username`, {
          params: { username: u },
          signal: controller.signal,
        });
        setStatus(res.data.available ? 'available' : { error: 'Username taken.' });
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return; // stale request, ignore
        console.error('[check-username]', err.response?.data || err.message);
        setStatus({ error: err.response?.data?.error || 'Could not check username.' });
      }
    }, 400);
  }, []);

  function handleChange(e) {
    const val = e.target.value.replace(/ /g, '_');
    setUsername(val);
    setError('');
    checkUsername(val);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (status !== 'available') return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/set-username`, { username: username.trim() });
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set username. Try again.');
    } finally {
      setLoading(false);
    }
  }

  function hint() {
    if (!username.trim()) return null;
    if (status === 'checking') return <span style={{ fontSize: 13, color: 'var(--cb-gray-400)' }}>Checking…</span>;
    if (status === 'available') return <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>✓ Available</span>;
    if (status?.error) return <span style={{ fontSize: 13, color: '#dc2626' }}>{status.error}</span>;
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cb-bg)' }}>

      {/* ── Static navbar (non-interactive) ── */}
      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <nav className="navbar lp-topnav" style={{ position: 'relative', zIndex: 10 }}>
          <div className="navbar-brand">
            <img src="/RANKEDSATLOGO.png" alt="RankedSAT" style={{ height: 'auto', width: 160, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <span className="navbar-link">Leaderboard</span>
            <span className="navbar-link">About</span>
          </div>
          <div className="navbar-right">
            <span className="btn btn-ghost btn-sm" style={{ fontWeight: 600, color: 'rgba(255,255,255,.85)' }}>Help</span>
          </div>
        </nav>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{
        background: 'var(--cb-white)', borderRadius: 16, padding: '36px 32px',
        maxWidth: 400, width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid var(--cb-gray-200)',
      }}>
        {/* Google avatar if available */}
        {user?.avatar && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <img src={user.avatar} alt="profile" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
          </div>
        )}

        <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: 'var(--cb-navy-text)', textAlign: 'center' }}>
          Choose a username
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--cb-gray-500)', textAlign: 'center' }}>
          This is how other players will see you. You can change it later.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--cb-gray-700)', marginBottom: 6 }}>
              Username
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. mathwiz99"
              value={username}
              onChange={handleChange}
              onKeyDown={e => { if (e.key === ' ') { e.preventDefault(); setUsername(v => (v + '_').slice(0, 16)); } }}
              maxLength={16}
              autoFocus
              required
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <div style={{ marginTop: 5 }}>{hint()}</div>
          </div>

          {error && (
            <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading || status !== 'available'}
          >
            {loading ? 'Saving…' : 'Continue'}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
}
